import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  RefreshCw, 
  Search,
  Calendar,
  MapPin,
  User,
  CreditCard,
  Eye,
  BookOpen,
  Clock,
  Users as UsersIcon,
  Filter,
  Grid3X3,
  LayoutList,
  MoreHorizontal,
  ArrowUpRight,
  School,
  ChevronDown,
  ChevronUp,
  Sparkles,
  TrendingUp,
  Activity,
  Target,
  Star,
  ArrowRight,
  Zap
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Class, Course, Instructor, Subscription } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from '@/components/ui/skeleton';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import PageContainer from '@/components/common/PageContainer';
import { StatusBadge } from '@/components/common/StatusBadge';

// Update the ClassStatus type to match the database constraints
type ClassStatus = 'draft' | 'scheduled' | 'cancelled' | 'finished';
// No view mode needed anymore - list view only

interface ClassWithRelations {
  id: string;
  course_id: string;
  subscription_id: string | null;
  location_address: string | null;
  status: ClassStatus;
  display_id?: string | null;
  courses?: Course;
  sessions?: any[];
}

interface ClassSession {
  // Fields from view
  class_session_id: string;
  class_session_status: string | null;
  start_date: string | null;
  end_date: string | null;
  duration_minutes: number;
  teams_meeting_url: string | null;
  mode: string;
  class_id: string;
  display_id: string | null;
  course_id: string;
  name: string;
  family_name: string | null;
  status: string;
  org_name: string;
  domain: string | null;
  created_at?: string; // Added created_at field
  
  // Session count from the improved view
  session_count?: number;
  
  // For processed data
  child_sessions?: ClassSession[];
  instructor_name?: string; // Added missing field that was used in the code
}

const Classes = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState<ClassSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  // View mode removed - using list view only
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentClass, setCurrentClass] = useState<ClassWithRelations | null>(null);
  
  // Additional state variables
  const [courses, setCourses] = useState<Course[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  
  // Form states
  const [formCourseId, setFormCourseId] = useState<string | undefined>(undefined);
  const [formSubscriptionId, setFormSubscriptionId] = useState<string | undefined>(undefined);
  const [formLocationAddress, setFormLocationAddress] = useState('');
  const [formStatus, setFormStatus] = useState<ClassStatus>('draft');

  useEffect(() => {
    fetchClasses();
    fetchCourses();
    fetchInstructors();
    fetchSubscriptions();
  }, []);

  const processClassSessions = (data: ClassSession[]) => {
    console.log('Raw data from view:', data);
    
    // First, let's look at a sample record to debug
    if (data.length > 0) {
      console.log('Sample record:', data[0]);
    }

    // Group sessions by class_id
    const groupedSessions = data.reduce((acc, session) => {
      // Ensure class_id exists
      if (!session.class_id) {
        console.log('Session without class_id:', session);
        return acc;
      }

      if (!acc[session.class_id]) {
        // Get all sessions for this class_id
        const classSessions = data.filter(s => s.class_id === session.class_id);
        
              // Find the main record - it should be the one with class_session_status as null or undefined
      const mainRecord = classSessions.find(s => !s.class_session_status) || classSessions[0];
      
      // Use the session_count from the view if available, otherwise use the total count of sessions
      if (mainRecord && !mainRecord.session_count) {
        // Use total count of sessions to match displayed count
        mainRecord.session_count = classSessions.length;
      }
      
      acc[session.class_id] = {
        main: mainRecord,
        // Include ALL sessions for display, including the main record
        sessions: classSessions
      };
      }
      
      return acc;
    }, {} as Record<string, { main: ClassSession | null; sessions: ClassSession[] }>);

    // Convert to array and filter out entries without main record
    const processedData = Object.values(groupedSessions)
      .filter(group => group.main !== null)
      .map(group => ({
        ...group.main!,
        child_sessions: group.sessions.sort((a, b) => 
          a.start_date && b.start_date 
            ? new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
            : 0
        )
      }));

    console.log('Processed data:', processedData);
    return processedData;
  };

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('class_sessions_')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const processedData = processClassSessions(data as ClassSession[]);
      // Ensure classes are sorted by created_at in descending order
      const sortedData = processedData.sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA; // Descending order
      });
      setClasses(sortedData);
    } catch (error) {
      console.error('Erro ao buscar formações:', error);
      toast.error('Não foi possível carregar as formações');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .order('name');

      if (error) throw error;
      
      setCourses(data as Course[]);
    } catch (error) {
      console.error('Erro ao buscar cursos:', error);
      toast.error('Não foi possível carregar os cursos');
    }
  };

  const fetchInstructors = async () => {
    try {
      const { data, error } = await supabase
        .from('instructors')
        .select('*')
        .order('first_name');

      if (error) throw error;
      
      setInstructors(data as Instructor[]);
    } catch (error) {
      console.error('Erro ao buscar instrutores:', error);
      toast.error('Não foi possível carregar os instrutores');
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          *,
          customer_relationship(
            id,
            customer:customer_id(id, name)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setSubscriptions(data as any as Subscription[]);
    } catch (error) {
      console.error('Erro ao buscar assinaturas:', error);
      toast.error('Não foi possível carregar as assinaturas');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateFilter('all');
    setStartDate('');
    setEndDate('');
    fetchClasses();
  };

  const handleSearch = () => {
    if (!searchTerm.trim() && statusFilter === 'all' && dateFilter === 'all' && !startDate && !endDate) {
      fetchClasses();
      return;
    }

    let filteredClasses = [...classes];
    
    // Apply text search filter
    if (searchTerm.trim()) {
      const searchTermLower = searchTerm.toLowerCase();
      filteredClasses = filteredClasses.filter(cls => {
        // Search in main record fields
        const mainRecordMatch = 
          cls.display_id?.toLowerCase().includes(searchTermLower) ||
          cls.name?.toLowerCase().includes(searchTermLower) ||
          cls.org_name?.toLowerCase().includes(searchTermLower) ||
          cls.domain?.toLowerCase().includes(searchTermLower);

        // Search in child sessions
        const childSessionsMatch = cls.child_sessions?.some(session => 
          session.class_session_id?.toLowerCase().includes(searchTermLower) ||
          session.mode?.toLowerCase().includes(searchTermLower) ||
          session.instructor_name?.toLowerCase().includes(searchTermLower) ||
          (session.duration_minutes?.toString() || '').includes(searchTermLower)
        );

        return mainRecordMatch || childSessionsMatch;
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filteredClasses = filteredClasses.filter(cls => {
        // If it's a main record status
        if (['draft', 'scheduled', 'cancelled', 'finished'].includes(statusFilter)) {
          return cls.status === statusFilter;
        }
        // If it's a session status
        else if (['expected', 'confirmed', 'rescheduled'].includes(statusFilter)) {
          return cls.child_sessions?.some(session => session.class_session_status === statusFilter);
        }
        return false;
      });
    }

    // Apply date filter
    if (dateFilter !== 'all' || (startDate && endDate)) {
      let filterStart: string;
      let filterEnd: string;

      if (dateFilter !== 'all') {
        const dateRange = getDateRange(dateFilter);
        filterStart = dateRange.start;
        filterEnd = dateRange.end;
      } else {
        filterStart = new Date(startDate).toISOString();
        filterEnd = new Date(endDate).toISOString();
      }

      filteredClasses = filteredClasses.filter(cls => {
        // Check if any child session falls within the date range
        return cls.child_sessions?.some(session => {
          if (!session.start_date) return false;
          const sessionDate = new Date(session.start_date).getTime();
          return sessionDate >= new Date(filterStart).getTime() && 
                 sessionDate <= new Date(filterEnd).getTime();
        });
      });
    }
    
    // Update total pages based on filtered results
    setTotalPages(Math.ceil(filteredClasses.length / itemsPerPage));
    
    // Get paginated data
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedClasses = filteredClasses.slice(startIndex, endIndex);
    
    setClasses(paginatedClasses);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter, startDate, endDate]);

  useEffect(() => {
    if (dateFilter !== 'all' || (startDate && endDate)) {
      handleSearch();
    }
  }, [statusFilter, dateFilter, startDate, endDate]);

  const openAddDialog = () => {
    setFormCourseId(undefined);
    setFormSubscriptionId(undefined);
    setFormLocationAddress('');
    setFormStatus('draft');
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (cls: ClassWithRelations) => {
    setCurrentClass(cls);
    setFormCourseId(cls.course_id);
    setFormSubscriptionId(cls.subscription_id);
    setFormLocationAddress(cls.location_address || '');
    setFormStatus(cls.status as ClassStatus);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (cls: ClassWithRelations) => {
    setCurrentClass(cls);
    setIsDeleteDialogOpen(true);
  };

  const handleAddClass = async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .insert([
          { 
            course_id: formCourseId || null,
            subscription_id: formSubscriptionId || null,
            location_address: formLocationAddress || null,
            status: formStatus,
          }
        ])
        .select();

      if (error) throw error;

      toast.success('Turma criada com sucesso');
      setIsAddDialogOpen(false);
      fetchClasses();
    } catch (error) {
      console.error('Erro ao criar turma:', error);
      toast.error('Não foi possível criar a turma');
    }
  };

  const handleEditClass = async () => {
    if (!currentClass) {
      return;
    }

    try {
      const { error } = await supabase
        .from('classes')
        .update({ 
          course_id: formCourseId || null,
          subscription_id: formSubscriptionId || null,
          location_address: formLocationAddress || null,
          status: formStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentClass.id);

      if (error) throw error;

      toast.success('Turma atualizada com sucesso');
      setIsEditDialogOpen(false);
      fetchClasses();
    } catch (error) {
      console.error('Erro ao atualizar turma:', error);
      toast.error('Não foi possível atualizar a turma');
    }
  };

  const handleDeleteClass = async () => {
    if (!currentClass) return;

    try {
      // Check if there are any sessions associated with this class
      const { count: sessionsCount, error: countError } = await supabase
        .from('class_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('class_id', currentClass.id);
      
      if (countError) throw countError;
      
      if (sessionsCount && sessionsCount > 0) {
        toast.error(`Não é possível excluir esta turma pois existem ${sessionsCount} sessões associadas a ela`);
        setIsDeleteDialogOpen(false);
        return;
      }

      // Check if there are any sponsors associated with this class
      const { count: sponsorsCount, error: sponsorsError } = await supabase
        .from('class_sponsors')
        .select('*', { count: 'exact', head: true })
        .eq('class_id', currentClass.id);
      
      if (sponsorsError) throw sponsorsError;
      
      if (sponsorsCount && sponsorsCount > 0) {
        toast.error(`Não é possível excluir esta turma pois existem ${sponsorsCount} patrocinadores associados a ela`);
        setIsDeleteDialogOpen(false);
        return;
      }

      const { error } = await supabase
        .from('classes')
        .delete()
        .eq('id', currentClass.id);

      if (error) throw error;

      toast.success('Turma excluída com sucesso');
      setIsDeleteDialogOpen(false);
      fetchClasses();
    } catch (error) {
      console.error('Erro ao excluir turma:', error);
      toast.error('Não foi possível excluir a turma');
    }
  };

  const viewClassDetails = (classId: string) => {
    navigate(`/classes/${classId}`);
  };

  const handleImageError = (classId: string) => {
    setFailedImages(prev => ({
      ...prev,
      [classId]: true
    }));
  };

  const toggleExpanded = (classId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(classId)) {
      newExpandedRows.delete(classId);
    } else {
      newExpandedRows.add(classId);
    }
    setExpandedRows(newExpandedRows);
  };

  const getDateRange = (period: string): { start: string; end: string } => {
    const today = new Date();
    const start = new Date();
    const end = new Date();

    switch (period) {
      case 'next7days':
        end.setDate(today.getDate() + 7);
        return { start: today.toISOString(), end: end.toISOString() };
      case 'next15days':
        end.setDate(today.getDate() + 15);
        return { start: today.toISOString(), end: end.toISOString() };
      case 'next30days':
        end.setDate(today.getDate() + 30);
        return { start: today.toISOString(), end: end.toISOString() };
      case 'currentMonth':
        start.setDate(1);
        end.setMonth(today.getMonth() + 1, 0);
        return { start: start.toISOString(), end: end.toISOString() };
      case 'currentWeek':
        start.setDate(today.getDate() - today.getDay());
        end.setDate(start.getDate() + 6);
        return { start: start.toISOString(), end: end.toISOString() };
      case 'currentYear':
        start.setMonth(0, 1);
        end.setMonth(11, 31);
        return { start: start.toISOString(), end: end.toISOString() };
      default:
        return { start: '', end: '' };
    }
  };

  const getInitials = (name: string): string => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getInstructorName = (cls: ClassWithRelations) => {
    // Se não houver sessões ou a primeira sessão não tiver instrutor, retornar '-'
    if (!cls.sessions || cls.sessions.length === 0 || !cls.sessions[0].instructors) return '-';
    
    // Retornar o nome do instrutor da primeira sessão
    const instructor = cls.sessions[0].instructors;
    return `${instructor.first_name} ${instructor.last_name}`;
  };

  const getNextSessionDate = (cls: ClassWithRelations) => {
    if (!cls.sessions || cls.sessions.length === 0) return null;
    
    // Filter future sessions and sort by start date
    const futureSessions = cls.sessions
      .filter(session => session.start_date && new Date(session.start_date) > new Date())
      .sort((a, b) => {
        if (!a.start_date || !b.start_date) return 0;
        return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
      });
    
    if (futureSessions.length > 0 && futureSessions[0].start_date) {
      return format(new Date(futureSessions[0].start_date), 'dd/MM/yyyy HH:mm');
    }
    
    return null;
  };

  const getSessionsCount = (cls: ClassWithRelations) => {
    return cls.sessions?.length || 0;
  };

  const getClassInfoCard = (cls: ClassWithRelations) => {
    const nextSessionDate = getNextSessionDate(cls);
    
    return (
      <Card key={cls.id} className="group overflow-hidden transition-all hover:shadow-md">
        <div className="relative">
          <div className="h-40 w-full bg-muted">
            {cls.courses?.cover_url ? (
              <img 
                src={cls.courses.cover_url} 
                alt={cls.courses?.name || 'Curso'} 
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://placehold.co/400x200?text=Imagem+do+Curso';
                }}
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center bg-primary/10">
                <School className="h-16 w-16 text-primary/40" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute top-2 right-2">
              <StatusBadge status={cls.status} context="general" />
            </div>
          </div>
          
          <div className="p-5">
            <h3 className="font-extrabold text-lg line-clamp-1 mb-1">
              {cls.courses?.name || "Curso não atribuído"}
            </h3>
            
            {cls.display_id && (
              <div className="mb-3">
                <code className="bg-primary/10 text-primary px-1.5 py-0.5 rounded text-sm font-mono font-extrabold">
                  {cls.display_id}
                </code>
              </div>
            )}
            
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-2 text-sm">
                <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <span className="text-muted-foreground">Instrutor:</span>
                <span className="font-extrabold flex-1">{getInstructorName(cls)}</span>
              </div>
              
              <div className="flex items-start gap-2 text-sm">
                <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <span className="text-muted-foreground">Próxima aula:</span>
                <span className="font-extrabold flex-1">{nextSessionDate || 'Não agendada'}</span>
              </div>
              
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <span className="text-muted-foreground">Local:</span>
                <span className="font-extrabold flex-1">
                  {cls.location_address ? cls.location_address : "Online"}
                </span>
              </div>
              
              <div className="flex items-start gap-2 text-sm">
                <BookOpen className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <span className="text-muted-foreground">Sessões:</span>
                <span className="font-extrabold flex-1">{getSessionsCount(cls)}</span>
              </div>
            </div>
          </div>
        </div>
        
        <CardFooter className="p-4 pt-0 flex justify-between">
          <Button 
            variant="ghost" 
            size="sm"
            className="gap-2"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/classes/${cls.id}`);
            }}
          >
            <Eye className="h-4 w-4" />
            Detalhes
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => openEditDialog(cls)}>
                <Pencil className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => openDeleteDialog(cls)}>
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardFooter>
      </Card>
    );
  };

  const getStatCards = () => {
    // Calculate stats
    const totalClasses = classes.length;
    const scheduledClasses = classes.filter(cls => cls.status === 'scheduled').length;
    const cancelledClasses = classes.filter(cls => cls.status === 'cancelled').length;
    const finishedClasses = classes.filter(cls => cls.status === 'finished').length;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 rounded-md">
                  <BookOpen className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-sm font-extrabold text-gray-600">Total de Formações</span>
              </div>
              <div className="text-2xl font-extrabold text-gray-900">
                {totalClasses}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Formações cadastradas
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-blue-500" />
                <span className="text-xs text-blue-600 font-extrabold">+12%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-green-100 rounded-md">
                  <Calendar className="h-4 w-4 text-green-600" />
                </div>
                <span className="text-sm font-extrabold text-gray-600">Formações Agendadas</span>
              </div>
              <div className="text-2xl font-extrabold text-gray-900">
                {scheduledClasses}
              </div>
            </div>
            <div className="space-y-2">
              <Progress 
                value={totalClasses > 0 ? (scheduledClasses / totalClasses) * 100 : 0} 
                className="h-1.5"
              />
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {totalClasses > 0 
                    ? `${Math.round((scheduledClasses / totalClasses) * 100)}% do total` 
                    : 'Nenhuma formação'}
                </div>
                <div className="flex items-center gap-1">
                  <Activity className="h-3 w-3 text-green-500" />
                  <span className="text-xs text-green-600 font-extrabold">+5%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-purple-100 rounded-md">
                  <Target className="h-4 w-4 text-purple-600" />
                </div>
                <span className="text-sm font-extrabold text-gray-600">Formações Concluídas</span>
              </div>
              <div className="text-2xl font-extrabold text-gray-900">
                {finishedClasses}
              </div>
            </div>
            <div className="space-y-2">
              <Progress 
                value={totalClasses > 0 ? (finishedClasses / totalClasses) * 100 : 0} 
                className="h-1.5"
              />
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {totalClasses > 0 
                    ? `${Math.round((finishedClasses / totalClasses) * 100)}% do total` 
                    : 'Nenhuma formação'}
                </div>
                <div className="flex items-center gap-1">
                  <Star className="h-3 w-3 text-purple-500" />
                  <span className="text-xs text-purple-600 font-extrabold">+8%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-orange-100 rounded-md">
                  <UsersIcon className="h-4 w-4 text-orange-600" />
                </div>
                <span className="text-sm font-extrabold text-gray-600">Taxa de Conclusão</span>
              </div>
              <div className="text-2xl font-extrabold text-gray-900">
                {totalClasses > 0
                  ? Math.round((finishedClasses / totalClasses) * 100)
                  : 0}%
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Finalizadas vs. Total
              </div>
              <div className="flex items-center gap-1">
                <Zap className="h-3 w-3 text-orange-500" />
                <span className="text-xs text-orange-600 font-extrabold">+3%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Formações</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Hero Header with Gradient Background */}
        <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
          {/* SVG Background Element */}
          <div className="absolute inset-0 pointer-events-none">
            <svg 
              className="opacity-30"
              width="1690"
              height="1026.5433"
              viewBox="0 0 826.54 502.06" 
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid slice"
            >
              <g transform="scale(1.1) translate(-70, -315)">
                <path d="M0 0h826.54v502.06H0z" fill="none"/>
                <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
                <defs>
                  <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity=".1"/>
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity=".1"/>
                  </linearGradient>
                </defs>
              </g>
            </svg>
          </div>
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <School className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white">Formações</h1>
                    <p className="text-blue-100 text-base pb-2">
                      Gerencie todas as formações e sessões da sua organização
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button 
                  variant="secondary" 
                  size="default" 
                  onClick={() => fetchClasses()} 
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
                <Button 
                  size="default" 
                  onClick={() => navigate('/class-wizard')} 
                  className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Formação
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="pl-3 lg:pl-4">
          <div className="container mx-auto px-6 -mt-4 relative z-10 space-y-8">

            {/* Enhanced Stats Cards with Modern Design */}
            {getStatCards()}

            {/* Enhanced Search and Filtering Section */}
            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="min-w-0 flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 text-gray-400 transform -translate-y-1/2" />
                        <Input
                          placeholder="Pesquisar formações..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            handleSearch();
                          }}
                          className="pl-10 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
              
                    <div className="flex gap-3">
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                          <div className="flex items-center gap-2">
                            <Filter className="h-4 w-4" />
                            <span>Status: {
                              statusFilter === 'all' ? 'Todos' :
                              statusFilter === 'draft' ? 'Rascunho' :
                              statusFilter === 'scheduled' ? 'Agendada' :
                              statusFilter === 'finished' ? 'Finalizada' :
                              statusFilter === 'cancelled' ? 'Cancelada' :
                              statusFilter === 'expected' ? 'Prevista' :
                              statusFilter === 'confirmed' ? 'Confirmada' :
                              statusFilter === 'rescheduled' ? 'Reagendada' :
                              'Desconhecido'
                            }</span>
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos os status</SelectItem>
                          <SelectItem value="draft">Rascunho</SelectItem>
                          <SelectItem value="scheduled">Agendada</SelectItem>
                          <SelectItem value="finished">Finalizada</SelectItem>
                          <SelectItem value="cancelled">Cancelada</SelectItem>
                          <SelectItem value="expected">Prevista</SelectItem>
                          <SelectItem value="confirmed">Confirmada</SelectItem>
                          <SelectItem value="rescheduled">Reagendada</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select 
                        value={dateFilter} 
                        onValueChange={(value) => {
                          setDateFilter(value);
                          if (value !== 'custom') {
                            setStartDate('');
                            setEndDate('');
                          }
                        }}
                      >
                        <SelectTrigger className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <span>Período: {
                              dateFilter === 'all' ? 'Todos' :
                              dateFilter === 'next7days' ? 'Próximos 7 dias' :
                              dateFilter === 'next15days' ? 'Próximos 15 dias' :
                              dateFilter === 'next30days' ? 'Próximos 30 dias' :
                              dateFilter === 'currentMonth' ? 'Mês atual' :
                              dateFilter === 'currentWeek' ? 'Semana atual' :
                              dateFilter === 'currentYear' ? 'Ano atual' :
                              dateFilter === 'custom' ? 'Personalizado' :
                              'Desconhecido'
                            }</span>
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos os períodos</SelectItem>
                          <SelectItem value="next7days">Próximos 7 dias</SelectItem>
                          <SelectItem value="next15days">Próximos 15 dias</SelectItem>
                          <SelectItem value="next30days">Próximos 30 dias</SelectItem>
                          <SelectItem value="currentMonth">Mês atual</SelectItem>
                          <SelectItem value="currentWeek">Semana atual</SelectItem>
                          <SelectItem value="currentYear">Ano atual</SelectItem>
                          <SelectItem value="custom">Personalizado</SelectItem>
                        </SelectContent>
                      </Select>

                      {dateFilter === 'custom' && (
                        <>
                          <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-40 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                          />
                          <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-40 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Enhanced Main Content with Modern Design */}
            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              {loading ? (
                <CardContent className="p-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[80px]"></TableHead>
                        <TableHead>ID da Formação</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Nome do Curso</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <TableRow key={index}>
                          <TableCell><Skeleton className="h-10 w-10 rounded-full" /></TableCell>
                          <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                          <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                          <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              ) : classes.length === 0 ? (
                <CardContent className="p-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <BookOpen className="h-12 w-12 text-blue-500" />
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-xl font-extrabold text-gray-900">
                        Nenhuma formação encontrada
                      </h3>
                      <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                        Não encontramos formações que correspondam aos critérios de busca.
                        Tente ajustar os filtros ou criar uma nova formação.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                      <Button variant="outline" onClick={fetchClasses}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Limpar filtros
                      </Button>
                      <Button onClick={() => navigate('/class-wizard')}>
                        <Plus className="h-4 w-4 mr-2" />
                        Nova Formação
                      </Button>
                    </div>
                  </div>
                </CardContent>
              ) : (
                <CardContent className="p-0">
                  <Table>
                  <TableHeader>
                    <TableRow>
                    <TableHead className="w-[80px]"></TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Nome do Curso</TableHead>
                    <TableHead>Nº de Aulas</TableHead>
                    <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classes.map((cls) => (
                    <React.Fragment key={cls.class_session_id}>
                      <TableRow 
                        className="group hover:bg-muted/40 cursor-pointer"
                        onClick={() => viewClassDetails(cls.class_id)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(cls.class_id);
                              }}
                            >
                              {expandedRows.has(cls.class_id) ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                            <Avatar className="h-10 w-10 border">
                              {cls.domain && !failedImages[cls.class_id] ? (
                                <AvatarImage 
                                  src={`https://cdn.brandfetch.io/${cls.domain}?c=1idk4AUs2-buvooOUvQ`}
                                  alt={cls.name}
                                  onError={() => handleImageError(cls.class_id)}
                                />
                              ) : (
                                <AvatarFallback className="bg-primary/10 text-primary">
                                  {getInitials(cls.name || 'N/A')}
                                </AvatarFallback>
                              )}
                            </Avatar>
                          </div>
                        </TableCell>
                        <TableCell className="font-extrabold">
                          <div className="font-mono text-sm">
                            {cls.display_id || `#${cls.class_id.substring(0, 8)}`}
                          </div>
                        </TableCell>
                        <TableCell>
                          {cls.org_name || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-xs text-muted-foreground">{cls.family_name || '-'}</span>
                            <span>{cls.name || 'N/A'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {/* Directly query all sessions for accurate count */}
                          {cls.session_count || 0}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={cls.status || 'draft'} context="class" />
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded section for sessions */}
                      {expandedRows.has(cls.class_id) && (
                        <TableRow>
                          <TableCell colSpan={8} className="p-0">
                            <div className="bg-muted/30 border-t">
                              <div className="p-4">
                                {cls.child_sessions && cls.child_sessions.length > 0 ? (
                                  <div className="space-y-2">
                                    {/* Column Headers */}
                                    <div className="grid gap-4 pb-2 text-xs font-extrabold text-muted-foreground border-b" 
                                         style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr' }}>
                                      <div>Data Início</div>
                                      <div>Data Fim</div>
                                      <div>Duração (min)</div>
                                      <div>Modo</div>
                                      <div>Instrutor</div>
                                      <div>Status</div>
                                    </div>
                                    
                                    {/* Session Rows */}
                                    {cls.child_sessions?.map((session, index) => (
                                      <div key={session.class_session_id} 
                                           className="grid gap-4 py-2 px-3 bg-background rounded" 
                                           style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr' }}>
                                        <div className="text-xs">
                                          {session.start_date ? format(new Date(session.start_date), 'dd/MM/yyyy HH:mm') : '-'}
                                        </div>
                                        <div className="text-xs">
                                          {session.end_date ? format(new Date(session.end_date), 'dd/MM/yyyy HH:mm') : '-'}
                                        </div>
                                        <div className="text-xs">
                                          {session.duration_minutes || '-'}
                                        </div>
                                        <div className="text-xs">
                                          {session.mode || '-'}
                                        </div>
                                        <div className="text-xs">
                                          {session.instructor_name || '-'}
                                        </div>
                                        <div className="text-xs">
                                          <StatusBadge 
                                            status={session.class_session_status || 'draft'} 
                                            context="class_session" 
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground">Nenhuma sessão encontrada</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                    ))}
                  </TableBody>
                </Table>

                    {/* Enhanced Pagination */}
                    <div className="py-4 border-t bg-gray-50/50">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious 
                              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={currentPage === 1}
                            />
                          </PaginationItem>
                          
                          {/* First Page */}
                          {currentPage > 2 && (
                            <PaginationItem>
                              <PaginationLink onClick={() => setCurrentPage(1)}>1</PaginationLink>
                            </PaginationItem>
                          )}

                          {/* Ellipsis */}
                          {currentPage > 3 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}

                          {/* Previous Page */}
                          {currentPage > 1 && (
                            <PaginationItem>
                              <PaginationLink onClick={() => setCurrentPage(currentPage - 1)}>
                                {currentPage - 1}
                              </PaginationLink>
                            </PaginationItem>
                          )}

                          {/* Current Page */}
                          <PaginationItem>
                            <PaginationLink isActive>{currentPage}</PaginationLink>
                          </PaginationItem>

                          {/* Next Page */}
                          {currentPage < totalPages && (
                            <PaginationItem>
                              <PaginationLink onClick={() => setCurrentPage(currentPage + 1)}>
                                {currentPage + 1}
                              </PaginationLink>
                            </PaginationItem>
                          )}

                          {/* Ellipsis */}
                          {currentPage < totalPages - 2 && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}

                          {/* Last Page */}
                          {currentPage < totalPages - 1 && (
                            <PaginationItem>
                              <PaginationLink onClick={() => setCurrentPage(totalPages)}>
                                {totalPages}
                              </PaginationLink>
                            </PaginationItem>
                          )}

                          <PaginationItem>
                            <PaginationNext 
                              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                              disabled={currentPage === totalPages}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                </CardContent>
              )}
            </Card>

            {/* Bottom Spacing */}
            <div className="h-16" />
          </div>
        </div>
      </div>
    </>
  );
};

export default Classes; 